import React, { useEffect, useState } from 'react';
import ReactFlow, { Controls, Background } from 'reactflow';
import 'reactflow/dist/style.css';
import dagre from 'dagre';

const nodeWidth = 180;
const nodeHeight = 50;

function getLayoutedElements(originalNodes, edges, direction = 'TB') {
  const dagreGraph = new dagre.graphlib.Graph(); // ✅ fresh graph every time
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ rankdir: direction });

  // ✅ clone and reset node positions
  const nodes = originalNodes.map((n) => ({
    ...n,
    position: { x: 0, y: 0 },
  }));

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  nodes.forEach((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    node.position = {
      x: nodeWithPosition.x - nodeWidth / 2,
      y: nodeWithPosition.y - nodeHeight / 2,
    };
  });

  return { nodes, edges };
}

export default function WorkflowFlowchart({
  queueData,
  selectedQueueSid,
  workflowData = [],
}) {
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);

  useEffect(() => {
    setReactFlowInstance(null);
  }, [selectedQueueSid]);

  useEffect(() => {
    let filteredQueueData = queueData;
    if (selectedQueueSid) {
      filteredQueueData = queueData.filter(
        (entry) =>
          entry.queueSid === selectedQueueSid ||
          queueData.some(
            (e) =>
              e.workflowName === entry.workflowName &&
              e.queueSid === selectedQueueSid
          )
      );
    }

    const workflowGroups = {};
    filteredQueueData.forEach((entry) => {
      if (!workflowGroups[entry.workflowName]) workflowGroups[entry.workflowName] = [];
      workflowGroups[entry.workflowName].push(entry);
    });

    let nodes = [];
    let edges = [];

    Object.entries(workflowGroups).forEach(([workflowName, entries]) => {
      const workflowConfig = workflowData.find(w => w.friendlyName === workflowName);
      const workflowTimeout = workflowConfig?.task_reservation_timeout ?? null;
      const workflowConfigRaw = workflowConfig?.configuration ?? null;

      const timeoutByQueue = {};
      if (workflowConfigRaw) {
        try {
          const config = JSON.parse(workflowConfigRaw);
          const filters = config?.task_routing?.filters ?? [];
          filters.forEach((filter) => {
            filter.targets.forEach((target) => {
              const sid = target.queue?.toLowerCase?.();
              if (sid) {
                timeoutByQueue[sid] = target.timeout ?? null;
              }
            });
          });
        } catch (e) {
          console.warn('Failed to parse workflow configuration', e);
        }
      }

      const uniqueQueues = {};
      entries.forEach(({ queueSid, queueName, priority }) => {
        uniqueQueues[queueSid] = { name: queueName, priority };
      });

    const workflowNodes = Object.entries(uniqueQueues).map(([id, { name, priority }]) => ({
    id,
    type: 'default',
    data: {
        label: (
        <div style={{ textAlign: 'center' }}>
            <div>{name}</div>
            <div style={{ fontSize: 12, color: '#555' }}>
            Priority: {priority ?? 'N/A'}
            </div>
        </div>
        ),
    },
    position: { x: 0, y: 0 },
    style: {
         padding: '7px 12px',
         border: id === selectedQueueSid ? '2px solid #f79251' : '1px solid #e3eaf3',
         borderRadius: 8,
         backgroundColor: id === selectedQueueSid ? '#f79251' : '#fff',
         color: id === selectedQueueSid ? '#fff' : '#2d4a7a',
         fontWeight: 500,
         boxShadow: '0 1px 6px rgba(44,62,80,0.04)',
         minWidth: 0,
    },
    }));

      const sorted = entries.sort((a, b) => a.targetIndex - b.targetIndex);

      const workflowEdges = [];
      for (let i = 0; i < sorted.length - 1; i++) {
        const sourceSid = sorted[i].queueSid;
        const targetSid = sorted[i + 1].queueSid;
        const timeout = timeoutByQueue[sourceSid.toLowerCase()] ?? workflowTimeout;

        workflowEdges.push({
          id: `e-${sourceSid}-${targetSid}`,
          source: sourceSid,
          target: targetSid,
          animated: true,
          style: { stroke: '#888' },
          label: `Timeout: ${
            timeout !== null && timeout !== undefined ? timeout + 's' : 'no timeout'
          }`,
          labelStyle: { fontWeight: 'bold', fontSize: 12, fill: '#333' },
        });
      }

      nodes = [...nodes, ...workflowNodes];
      edges = [...edges, ...workflowEdges];
    });

    const uniqueNodes = Array.from(new Map(nodes.map((n) => [n.id, n])).values());
    const uniqueEdges = Array.from(new Map(edges.map((e) => [e.id, e])).values());

    const layouted = getLayoutedElements(uniqueNodes, uniqueEdges, 'TB');
    setNodes(layouted.nodes);
    setEdges(layouted.edges);
  }, [queueData, selectedQueueSid, workflowData]);

  useEffect(() => {
    if (reactFlowInstance && nodes.length > 0) {
        reactFlowInstance.fitView({ padding: 0.2, maxZoom: 1.5 });
      return 
    }
  }, [nodes, edges, reactFlowInstance]);

  return (
    <div style={{ height: '100%', background: '#f7fafd', borderRadius: 14, boxShadow: '0 2px 12px rgba(44,62,80,0.07)', padding: 18, border: '1px solid #e3eaf3' }}>
      <ReactFlow
        key={selectedQueueSid}
        nodes={nodes}
        edges={edges}
        onInit={setReactFlowInstance}
        attributionPosition="bottom-left"
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
      >
        <Controls />
        <Background gap={0} color='white' />
      </ReactFlow>
    </div>
  );
}
